import { DatePipe } from '@angular/common';

const WEEKDAY_SHORT_TOKEN = /(?<!E)E{1,3}(?!E)/;

export function isGermanLocale(locale: string): boolean {
  return (locale ?? '').toLowerCase().startsWith('de');
}

/**
 * Formats a date with `DatePipe`. The Angular `de` locale data writes the
 * abbreviated weekday (`E`) with a trailing dot (`Di.`), the design sheet
 * wants `Di`, so for German the dot is dropped. French keeps its dot
 * (`mar.`) as in the sheet.
 */
export function formatLocalizedDate(
  value: Date | string | number,
  format: string,
  locale: string
): string | null {
  const datePipe = new DatePipe(locale);

  if (!isGermanLocale(locale) || !WEEKDAY_SHORT_TOKEN.test(format)) {
    return datePipe.transform(value, format);
  }

  const weekday = datePipe.transform(value, 'E');

  if (weekday === null) {
    return null;
  }

  return datePipe.transform(
    value,
    format.replace(WEEKDAY_SHORT_TOKEN, `'${weekday.replace('.', '')}'`)
  );
}

// Month abbreviations as defined by the "Date / Times Format" design sheet.
// `Intl` differs (en-GB `Sept`, de `März`/`Juni`/`Sept.`), so they are fixed here.
const MONTHS_SHORT: Record<string, string[]> = {
  en: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ],
  de: [
    'Jan',
    'Feb',
    'Mär',
    'Apr',
    'Mai',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Okt',
    'Nov',
    'Dez',
  ],
  fr: [
    'janv.',
    'févr.',
    'mars',
    'avr.',
    'mai',
    'juin',
    'juil.',
    'août',
    'sept.',
    'oct.',
    'nov.',
    'déc.',
  ],
};

// Weekday abbreviations as defined by the design sheet, indexed like
// `Date.getDay()` (0 = Sunday).
const WEEKDAYS_SHORT: Record<string, string[]> = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  fr: ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'],
};

export function getWeekdayShortName(dayIndex: number, locale: string): string {
  const names = WEEKDAYS_SHORT[(locale ?? '').toLowerCase().slice(0, 2)];

  return (
    names?.[dayIndex] ??
    new Intl.DateTimeFormat((locale ?? 'en').replaceAll('_', '-'), {
      weekday: 'short',
    }).format(new Date(2021, 7, 1 + dayIndex))
  );
}

export function getMonthShortName(monthIndex: number, locale: string): string {
  const names = MONTHS_SHORT[(locale ?? '').toLowerCase().slice(0, 2)];

  return (
    names?.[monthIndex] ??
    new Intl.DateTimeFormat((locale ?? 'en').replaceAll('_', '-'), {
      month: 'short',
    }).format(new Date(2021, monthIndex, 1))
  );
}
